import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Context } from '../AccelProvider/AccelProvider';
import { FormCaptchaChallenge, FormCaptchaType } from '../../models/captcha/FormCaptcha';
import styles from './FormCaptcha.module.scss';

export type FormCaptchaProps = {
    onCreate: (type: FormCaptchaType) => Promise<FormCaptchaChallenge | null>;
    onSolve: (challengeId: string, positionX: number, solveMs: number) => Promise<string | null>;
    onChange: (token: string | null) => void;
    type?: FormCaptchaType;
    errorText?: string;
    className?: string;
}

type NaturalSize = {
    backgroundWidth: number;
    backgroundHeight: number;
    pieceWidth: number;
}

const FormCaptcha: React.FC<FormCaptchaProps> = ({ onCreate, onSolve, onChange, type, errorText, className }) => {
    const { loc } = useContext(Context);

    const [challenge, setChallenge] = useState<FormCaptchaChallenge | null>(null);
    const [positionX, setPositionX] = useState(0);
    const [natural, setNatural] = useState<NaturalSize | null>(null);
    const [solved, setSolved] = useState(false);
    const [busy, setBusy] = useState(true);
    const [dragging, setDragging] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const backgroundRef = useRef<HTMLImageElement>(null);
    const pieceRef = useRef<HTMLImageElement>(null);
    const positionRef = useRef(0);
    const interactionStartedRef = useRef(0);
    const dragRef = useRef<{ pointerId: number; startClientX: number; startPosition: number } | null>(null);

    const failedText = useMemo(
        () => errorText || loc.word('Captcha.failed', { default: 'Verification failed, try again' }),
        [errorText]
    );

    const maxPositionX = useMemo(
        () => natural ? Math.max(0, natural.backgroundWidth - natural.pieceWidth) : 0,
        [natural]
    );

    const applyPosition = useCallback((value: number) => {
        const next = Math.min(maxPositionX, Math.max(0, value));
        positionRef.current = next;
        setPositionX(next);
    }, [maxPositionX]);

    const load = useCallback(async () => {
        setBusy(true);
        setSolved(false);
        setNatural(null);
        positionRef.current = 0;
        interactionStartedRef.current = 0;
        setPositionX(0);
        onChange(null);

        const result = await onCreate(type || FormCaptchaType.Puzzle);
        if (!result?.payload?.backgroundImageBytes) {
            setError(loc.word('Captcha.unavailable', { default: 'Verification is temporarily unavailable' }));
            setBusy(false);
            return;
        }

        setChallenge(result);
        setBusy(false);
    }, [type]);

    const solve = useCallback(async (value: number) => {
        if (!challenge) return;

        setBusy(true);
        const token = await onSolve(challenge.id, Math.round(value), interactionStartedRef.current ? Date.now() - interactionStartedRef.current : 0);
        if (token) {
            setSolved(true);
            setError(null);
            setBusy(false);
            onChange(token);
            return;
        }

        setError(failedText);
        await load();
    }, [challenge, failedText, load]);

    const onImageLoad = useCallback(() => {
        const background = backgroundRef.current;
        const piece = pieceRef.current;
        if (!background?.naturalWidth || !piece?.naturalWidth) return;

        setNatural({
            backgroundWidth: background.naturalWidth,
            backgroundHeight: background.naturalHeight,
            pieceWidth: piece.naturalWidth
        });
    }, []);

    const onPointerDown = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
        if (busy || solved || !natural) return;

        if (!interactionStartedRef.current) interactionStartedRef.current = Date.now();
      dragRef.current = { pointerId: e.pointerId, startClientX: e.clientX, startPosition: positionRef.current };
        pieceRef.current?.setPointerCapture?.(e.pointerId);
        setDragging(true);
        e.preventDefault();
    }, [busy, solved, natural]);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId || !natural) return;

        const displayWidth = backgroundRef.current?.clientWidth || natural.backgroundWidth;
        const ratio = displayWidth / natural.backgroundWidth;

        applyPosition(drag.startPosition + (e.clientX - drag.startClientX) / ratio);
        e.preventDefault();
    }, [natural, applyPosition]);

    const onPointerUp = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;

        dragRef.current = null;
        pieceRef.current?.releasePointerCapture?.(e.pointerId);
        setDragging(false);
        solve(positionRef.current);
    }, [solve]);

    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLImageElement>) => {
        if (busy || solved || !natural) return;

        const step = e.shiftKey ? 10 : 1;

        if (e.key === 'ArrowLeft') {
            applyPosition(positionRef.current - step);
            e.preventDefault();
            return;
        }

        if (e.key === 'ArrowRight') {
            applyPosition(positionRef.current + step);
            e.preventDefault();
            return;
        }

        if (e.key === 'Enter' || e.key === ' ') {
            solve(positionRef.current);
            e.preventDefault();
        }
    }, [busy, solved, natural, applyPosition, solve]);

    useEffect(() => { load(); }, []);

    const statusClassName = useMemo(() => {
        if (error) return `${styles.status} ${styles.error}`;
        if (solved) return `${styles.status} ${styles.solved}`;
        return styles.status;
    }, [error, solved]);

    const statusText = useMemo(() => {
        if (error) return error;
        if (solved) return loc.word('Captcha.solved', { default: 'Verified' });
        return loc.word('Captcha.helpText', { default: 'Drag the piece into the gap' });
    }, [error, solved]);

    const pieceStyle = useMemo(() => {
        if (!natural || !challenge) return undefined;

        return {
            top: `${(challenge.payload.positionY / natural.backgroundHeight) * 100}%`,
            left: `${(positionX / natural.backgroundWidth) * 100}%`,
            width: `${(natural.pieceWidth / natural.backgroundWidth) * 100}%`
        };
    }, [natural, challenge, positionX]);

    const frameClassName = useMemo(
        () => dragging ? `${styles.frame} ${styles.dragging}` : styles.frame,
        [dragging]
    );

    return <div className={className}>
        {challenge && !solved && <div className={frameClassName}>
            <div className={styles.stage}>
                <img className={styles.background}
                    ref={backgroundRef}
                    onLoad={onImageLoad}
                    src={`data:image/jpeg;base64,${challenge.payload.backgroundImageBytes}`}
                    alt=''
                    draggable={false} />
                <img className={styles.piece}
                    ref={pieceRef}
                    onLoad={onImageLoad}
                    style={pieceStyle}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onKeyDown={onKeyDown}
                    tabIndex={0}
                    role='slider'
                    aria-label={statusText}
                    aria-valuemin={0}
                    aria-valuemax={Math.round(maxPositionX)}
                    aria-valuenow={Math.round(positionX)}
                    src={`data:image/png;base64,${challenge.payload.puzzleImageBytes}`}
                    alt=''
                    draggable={false} />
            </div>
        </div>}

        <div className={statusClassName}>{statusText}</div>
    </div>;
}

export default FormCaptcha;
